import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { AddCircleOutline as AddIcon, CloseOutlined } from '@mui/icons-material';

import { FHButton, FHInputField } from '../../../base';
import { clientFamilyBackgroundActions } from '../../../../store';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import { Themes } from '../../../../constants';

import type { RootState } from '../../../../store';

function Strengths() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { strengths = [] } = useSelector((state: RootState) => state.clientFamilyBackgrounds);

  const [newStrength, setNewStrength] = useState('');

  useEffect(() => {
    const isError = isEditable && strengths?.length === 0;
    dispatch(
      clientFamilyBackgroundActions.updateState({
        key: 'strengthErrorCount',
        data: isError ? 1 : 0,
      }),
    );
  }, [isEditable, strengths]);

  const handleAddStrength = async () => {
    if (newStrength.length < 2) {
      toast.error('Strength must be at least 2 characters');
      return;
    }
    if (strengths?.some((strength) => strength.toLowerCase() === newStrength.toLowerCase())) {
      toast.error('Strength already exists');
      return;
    }

    try {
      const updatedStrengths = [...(strengths || []), newStrength];

      const res = await dispatch(
        clientFamilyBackgroundActions.updateStrengths({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            client_strengths: updatedStrengths,
          },
          getAccessTokenSilently,
        }),
      ).unwrap();

      if (!res.error) {
        setNewStrength('');
        toast.success('Strength added successfully');
      }
    } catch {
      toast.error('Failed to add strength');
    }
  };

  const handleDeleteStrength = async (index: number) => {
    if (!strengths) return;

    try {
      const updatedStrengths = strengths.filter((_, i) => i !== index);
      const res = await dispatch(
        clientFamilyBackgroundActions.updateStrengths({
          treatmentPlanId: treatmentPlan?.id,
          data: {
            client_strengths: updatedStrengths,
          },
          getAccessTokenSilently,
        }),
      ).unwrap();

      if (!res.error) {
        toast.success('Strength deleted successfully');
      }
    } catch {
      toast.error('Failed to delete strength');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Typography fontSize={18} fontWeight="bold" textAlign="left">
        Strengths/Weaknesses*
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '16px' }}>
        <FHInputField
          data-testid="strengthsTextField"
          value={newStrength}
          sx={{ width: '250px' }}
          onChange={(e) => setNewStrength(e.target.value)}
          disabled={!isEditable}
          fullWidth
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddStrength();
            }
          }}
        />
        <FHButton
          data-testid="strengthsAddButton"
          colorTheme={Themes.Primary}
          startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
          sx={{ width: 'fit-content' }}
          disabled={!isEditable}
          onClick={handleAddStrength}
          error={strengths?.length === 0}
        >
          ADD
        </FHButton>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        {strengths &&
          strengths.length > 0 &&
          strengths.map((strength, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '6px',
              }}
            >
              <Typography pl={1} fontSize={16}>
                {strength}
              </Typography>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ ml: 2, my: 0.1, borderColor: FORTA_BLUE.light1 }}
              />
              {isEditable && (
                <IconButton
                  data-testid="strengthsDeleteButton"
                  onClick={() => handleDeleteStrength(index)}
                  sx={{
                    padding: '4px',
                    '&:hover': { opacity: 0.8 },
                  }}
                >
                  <CloseOutlined sx={{ color: FORTA_BLUE.primary }} />
                </IconButton>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  );
}

export default Strengths;
